import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react';
import Header from './components/Header22';
import Footer from './components/Footer22';

const Signup22 = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            return;
        }
        navigate('/template22');
    };

    const passwordStrength = useMemo(() => {
        const pwd = formData.password;
        if (!pwd) return { level: 0, label: '', color: '' };

        let score = 0;
        if (pwd.length >= 8) score++;
        if (pwd.length >= 12) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;

        if (score <= 2) return { level: 1, label: 'Weak', color: '#D32F2F' };
        if (score <= 4) return { level: 2, label: 'Fair', color: '#F59E0B' };
        return { level: 3, label: 'Strong', color: '#2E7D32' };
    }, [formData.password]);

    const passwordsMatch = formData.confirmPassword
        ? formData.password === formData.confirmPassword
        : true;

    const inputBaseClass = "w-full px-4 py-3.5 bg-white border border-[#E8DDD0] text-[#5C3D2E] text-sm rounded-lg outline-none transition-colors duration-300 focus:border-[#C4572A] placeholder-[#B0A090]";

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ fontFamily: "'Source Sans 3', -apple-system, sans-serif" }} className="min-h-screen bg-[#FAF6F1]">
                <Header />

                <main className="pt-20 min-h-screen flex">
                    {/* Left Side - Hero Image (Desktop Only) */}
                    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/8838890/pexels-photo-8838890.jpeg?auto=compress&cs=tinysrgb&w=1920"
                            alt="Indian artisan weaving"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF6F1]/20 via-[#FAF6F1]/50 to-[#FAF6F1]" />
                        <div className="relative z-10 flex flex-col justify-center items-start p-16 max-w-lg">
                            <div className="w-12 h-px bg-[#C4572A] mb-6" />
                            <h2
                                style={{ fontFamily: "'Cormorant Infant', Georgia, serif" }}
                                className="text-4xl xl:text-5xl font-light text-[#5C3D2E] leading-tight mb-4"
                            >
                                Join the Craft Movement
                            </h2>
                            <p className="text-[#8B7355] text-base leading-relaxed">
                                Become part of a community that values handmade traditions, supports artisan livelihoods, and wears heritage with pride.
                            </p>
                        </div>
                    </div>

                    {/* Right Side - Signup Form */}
                    <div className="flex-1 flex items-center justify-center px-6 py-12 lg:py-0">
                        <div className="w-full max-w-md">
                            {/* Brand */}
                            <div className="text-center mb-8">
                                <h1
                                    onClick={() => navigate('/template22')}
                                    style={{ fontFamily: "'Cormorant Infant', Georgia, serif" }}
                                    className="text-3xl font-semibold text-[#C4572A] cursor-pointer tracking-[4px] mb-3"
                                >
                                    MAATI
                                </h1>
                                <h2
                                    style={{ fontFamily: "'Cormorant Infant', Georgia, serif" }}
                                    className="text-2xl lg:text-3xl font-light text-[#5C3D2E] tracking-wide"
                                >
                                    Create Account
                                </h2>
                                <p className="text-[#8B7355] text-sm mt-2">
                                    Start your journey into Indian handcraft heritage
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-[#5C3D2E] text-sm font-medium mb-2">Full Name</label>
                                    <div className="relative">
                                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Your full name"
                                            className={`${inputBaseClass} pl-12`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[#5C3D2E] text-sm font-medium mb-2">Email</label>
                                    <div className="relative">
                                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="you@example.com"
                                            className={`${inputBaseClass} pl-12`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[#5C3D2E] text-sm font-medium mb-2">Phone</label>
                                    <div className="relative">
                                        <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="+91 98765 43210"
                                            className={`${inputBaseClass} pl-12`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[#5C3D2E] text-sm font-medium mb-2">Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Create a strong password"
                                            className={`${inputBaseClass} pl-12 pr-12`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#C4572A] transition-colors"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {/* Password Strength */}
                                    {formData.password && (
                                        <div className="mt-2">
                                            <div className="flex gap-1.5 mb-1">
                                                {[1, 2, 3].map(level => (
                                                    <div
                                                        key={level}
                                                        className="h-1 flex-1 rounded-full transition-colors"
                                                        style={{
                                                            backgroundColor: passwordStrength.level >= level
                                                                ? passwordStrength.color
                                                                : '#E8DDD0',
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                            <p className="text-xs" style={{ color: passwordStrength.color }}>
                                                {passwordStrength.label}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-[#5C3D2E] text-sm font-medium mb-2">Confirm Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Confirm your password"
                                            className={`${inputBaseClass} pl-12 pr-12`}
                                            style={{
                                                borderColor: !passwordsMatch ? '#D32F2F' : undefined,
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#C4572A] transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {!passwordsMatch && (
                                        <p className="text-xs mt-1" style={{ color: '#D32F2F' }}>Passwords do not match</p>
                                    )}
                                </div>

                                <div className="flex items-start gap-2 pt-1">
                                    <input
                                        type="checkbox"
                                        required
                                        className="w-4 h-4 mt-0.5 rounded border-[#E8DDD0] bg-white accent-[#C4572A]"
                                    />
                                    <span className="text-[#8B7355] text-sm leading-snug">
                                        I agree to the{' '}
                                        <button
                                            type="button"
                                            onClick={() => navigate('/template22/terms')}
                                            className="text-[#C4572A] hover:text-[#D4683B] transition-colors"
                                        >
                                            Terms & Conditions
                                        </button>{' '}
                                        and{' '}
                                        <button
                                            type="button"
                                            onClick={() => navigate('/template22/privacy')}
                                            className="text-[#C4572A] hover:text-[#D4683B] transition-colors"
                                        >
                                            Privacy Policy
                                        </button>
                                    </span>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full py-4 bg-[#C4572A] text-white text-sm font-semibold tracking-wider uppercase rounded-lg hover:bg-[#D4683B] transition-colors duration-300"
                                >
                                    Create Account
                                </button>
                            </form>

                            {/* Divider */}
                            <div className="flex items-center my-6">
                                <div className="flex-1 h-px bg-[#E8DDD0]" />
                                <span className="px-4 text-[#8B7355] text-xs tracking-wider uppercase">or</span>
                                <div className="flex-1 h-px bg-[#E8DDD0]" />
                            </div>

                            {/* Google Signup */}
                            <button className="w-full flex items-center justify-center gap-3 px-6 py-3.5 border border-[#E8DDD0] text-[#5C3D2E] text-sm rounded-lg hover:border-[#C4572A] transition-colors duration-300">
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Sign up with Google
                            </button>

                            {/* Login Link */}
                            <p className="text-center mt-6 mb-8 text-[#8B7355] text-sm">
                                Already have an account?{' '}
                                <button
                                    onClick={() => navigate('/template22/login')}
                                    className="text-[#C4572A] font-medium hover:text-[#D4683B] transition-colors"
                                >
                                    Sign in
                                </button>
                            </p>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Signup22;
